import { Component, Input, OnInit } from '@angular/core';
import { CommonModule } from '@angular/common';
import { GalaxyPopoverModule, PopoverPositions } from '@vendasta/galaxy/popover';
import { MatCardModule } from '@angular/material/card';
import { MatIconModule } from '@angular/material/icon';
import { NgApexchartsModule } from 'ng-apexcharts';
import { TranslateModule } from '@ngx-translate/core';
import { Observable } from 'rxjs';
import { map } from 'rxjs/operators';

export interface NPSRollingAverageDataPoints {
  netPromoterScoreDate: Date;
  rollingAverage: number;
}
export interface NPSRollingAverageChartOptions {
  series: {
    name: string;
    data: { x: number; y: number }[];
  }[];
  chart: ApexChart;
  dataLabels: ApexDataLabels;
  markers: ApexMarkers;
  xaxis: ApexXAxis;
  tooltip: ApexTooltip;
  colors: string[];
  fill: ApexFill;
  stroke: ApexStroke;
  grid: {
    show: boolean;
    borderColor: string;
    strokeDashArray: number;
    position: string;
    xaxis: {
      lines: {
        show: boolean;
        borderColor: string;
      };
    };
    yaxis: {
      lines: {
        show: boolean;
        borderColor: string;
      };
      min: number;
      max: number;
      tickAmount: number;
      labels: {
        formatter: (value: number) => string; // Rounds off to the nearest integer
        style: {
          colors: string;
        };
      };
    };
  };
  shouldRenderChart: boolean;
}

@Component({
  selector: 'reviews-nps-rolling-average',
  imports: [GalaxyPopoverModule, MatCardModule, MatIconModule, NgApexchartsModule, TranslateModule, CommonModule],
  templateUrl: './nps-rolling-average.component.html',
  styleUrl: './nps-rolling-average.component.scss',
})
export class NpsRollingAverageComponent implements OnInit {
  @Input() npsRollingAvgData$: Observable<NPSRollingAverageDataPoints[]>;
  @Input() displayEmptyChart = false;

  rollingAvgChartOptions$: Observable<NPSRollingAverageChartOptions>;
  showPopover = false;
  PopoverPositions = PopoverPositions;

  private dataLabelColor = '#616161FF';
  private borderColor = '#e0e0e0';

  ngOnInit(): void {
    this.rollingAvgChartOptions$ = this.npsRollingAvgData$.pipe(
      map((sortedDataPoints) => {
        // Calculate the min and max from the sortedDataPoints array
        const dataValues = sortedDataPoints.map((point) => point.rollingAverage);
        const dynamicMin = dataValues.length ? Math.floor(Math.min(...dataValues)) : 0;
        const dynamicMax = dataValues.length ? Math.ceil(Math.max(...dataValues)) : 10;
        const shouldRenderChart = dataValues.length > 0 || this.displayEmptyChart;
        return {
          ...this.getDefaultChartOptions(dynamicMin, dynamicMax, shouldRenderChart),
          series: [
            {
              name: 'Average',
              data: sortedDataPoints.length
                ? sortedDataPoints.map((point) => ({
                    x: new Date(point.netPromoterScoreDate).getTime(),
                    y: point.rollingAverage,
                  }))
                : [{ x: new Date().getTime(), y: null }],
            },
          ],
        };
      }),
    );
  }

  private getDefaultChartOptions(
    dynamicMin: number,
    dynamicMax: number,
    shouldRenderChart: boolean,
  ): NPSRollingAverageChartOptions {
    return {
      series: [
        {
          name: 'Average',
          data: [],
        },
      ],
      chart: {
        type: 'area',
        height: 140,
        width: '100%',
        toolbar: {
          show: false,
        },
        zoom: {
          enabled: false,
        },
        animations: {
          enabled: false,
        },
      },
      dataLabels: {
        enabled: false,
      },
      markers: {
        size: 0,
      },
      xaxis: {
        type: 'datetime',
        tickAmount: 6,
        labels: {
          datetimeFormatter: {
            day: 'MMM dd',
          },
          style: {
            colors: this.dataLabelColor,
          },
        },
      },
      tooltip: {
        x: {
          format: 'dd MMM yyyy',
        },
      },
      colors: ['#42A5F5'],
      fill: {
        type: 'gradient',
        gradient: {
          shadeIntensity: 1,
          opacityFrom: 0.6,
          opacityTo: 0.3,
          stops: [0, 50],
        },
      },
      stroke: {
        width: 2,
      },
      grid: {
        show: true,
        borderColor: this.borderColor,
        strokeDashArray: 0,
        position: 'back',
        xaxis: {
          lines: {
            show: true,
            borderColor: this.borderColor,
          },
        },
        yaxis: {
          lines: {
            show: true,
            borderColor: this.borderColor,
          },
          min: dynamicMin,
          max: dynamicMax,
          tickAmount: 2,
          labels: {
            formatter: (value: number): string => {
              return (value * 10).toFixed(0);
            },
            style: {
              colors: this.dataLabelColor,
            },
          },
        },
      },
      shouldRenderChart,
    };
  }

  showPopovers(): void {
    this.showPopover = true;
  }

  hidePopovers(): void {
    this.showPopover = false;
  }
}
